import { useMemo } from 'react';
import { TaskType } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { i18next, useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { IconName } from '@frontend/icons';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';
import { theme } from '@frontend/theme';
import { CategoryChipVariants, ChipVariants } from '@frontend/design-system';

// Helper for translations
const t = (key: string) => i18next.t(key, { ns: 'analytics' });

//types
export type CallIntelChipVariant<T extends ChipVariants | CategoryChipVariants = ChipVariants> = Record<
  string,
  {
    variant?: T;
    variantColor?: string;
    leftIconName?: IconName;
    fullWidth?: boolean;
    textColor?: string;
    leftIconColor?: string;
    backgroundColor?: string;
    border?: string;
    borderStyle?: React.CSSProperties['borderStyle'];
  }
>;

export type DataLabelsAndColors = {
  appointmentTypes?: Record<CallIntelTypes.AppointmentTypeEnum | string, string>;
  categories?: Record<CallIntelTypes.CategoryEnum | string, string>;
  contactType?: Record<CallIntelTypes.ContactTypeEnum | string, string>;
  schedulingOutcome?: Record<CallIntelTypes.SchedulingOutcomeEnum | string, string>;
  schedulingOutcomeV2?: Record<CallIntelTypes.SchedulingOutcomeV2Enum | string, string>;
  schedulingOutcomeV2Alternate?: Record<CallIntelTypes.SchedulingOutcomeV2Enum | string, string>;
  sentiments?: Record<CallIntelTypes.SentimentEnum | string, string>;
  sentimentsWithEmoji?: Record<CallIntelTypes.SentimentEnum | string, string>;
  serviceQualityFlags?: Record<CallIntelTypes.ServiceQualityFlagEnum | string, string>;
  serviceQualityLabels?: Record<CallIntelTypes.ServiceQualityFlagEnum | string, string>;
  taskTypes?: Record<TaskType | string, string>;
};

export type PageLabels = {
  serviceQualityFlags?: Record<CallIntelTypes.ServiceQualityFlagEnum | string, string>;
};
type DataLabels = Omit<DataLabelsAndColors, 'contactType'>;

const DEFAULT_APPOINTMENT_TYPES: CallIntelTypes.AppointmentTypeEnum[] = [
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_IMAGING,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_COSMETIC,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_ORTHODONTICS,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SURGERY,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_HYGIENE,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_RESTORATIVE,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_EMERGENCY,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SICK_PET,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE_CARE,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DENTAL,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTICS,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_END_OF_LIFE_CARE,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_EYE_EXAM,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_VISION_CORRECTION,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_MEDICAL_EYE_CARE,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SPECIALTY_SERVICES,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_CONSULTATION,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_ROUTINE_CHECKUP,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTIC_TESTS,
  CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_OTHER,
];

const DEFAULT_CATEGORIES: CallIntelTypes.CategoryEnum[] = [
  CallIntelTypes.CategoryEnum.CATEGORY_BILLING,
  CallIntelTypes.CategoryEnum.CATEGORY_COMPLAINT,
  CallIntelTypes.CategoryEnum.CATEGORY_EMERGENCY,
  CallIntelTypes.CategoryEnum.CATEGORY_MEDICATION,
  CallIntelTypes.CategoryEnum.CATEGORY_OTHER,
  CallIntelTypes.CategoryEnum.CATEGORY_SCHEDULING,
  CallIntelTypes.CategoryEnum.CATEGORY_UNKNOWN,
  CallIntelTypes.CategoryEnum.CATEGORY_VENDOR,
];

const DEFAULT_CHART_LABEL_COLORS: DataLabelsAndColors = {
  appointmentTypes: {
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_COSMETIC]: theme.colors.secondary.seaweed50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_EMERGENCY]: theme.colors.critical50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_HYGIENE]: theme.colors.indigo50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_IMAGING]: theme.colors.tangerine50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_ORTHODONTICS]: theme.colors.secondary.eggplant50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE]: theme.colors.rose50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_RESTORATIVE]: theme.colors.tangerine50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SURGERY]: theme.colors.secondary.seaweed50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN]: theme.colors.neutral50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SICK_PET]: theme.colors.tangerine50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE_CARE]: theme.colors.rose50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DENTAL]: theme.colors.indigo50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTICS]: theme.colors.secondary.seaweed50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_END_OF_LIFE_CARE]: theme.colors.secondary.eggplant50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_EYE_EXAM]: theme.colors.indigo50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_VISION_CORRECTION]: theme.colors.tangerine50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_MEDICAL_EYE_CARE]: theme.colors.rose50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SPECIALTY_SERVICES]: theme.colors.secondary.eggplant50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_CONSULTATION]: theme.colors.rose50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_ROUTINE_CHECKUP]: theme.colors.indigo50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTIC_TESTS]: theme.colors.tangerine50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_OTHER]: theme.colors.neutral50,
  },

  categories: {
    [CallIntelTypes.CategoryEnum.CATEGORY_BILLING]: theme.colors.success50,
    [CallIntelTypes.CategoryEnum.CATEGORY_COMPLAINT]: theme.colors.warning50,
    [CallIntelTypes.CategoryEnum.CATEGORY_EMERGENCY]: theme.colors.critical50,
    [CallIntelTypes.CategoryEnum.CATEGORY_MEDICATION]: theme.colors.secondary.eggplant50,
    [CallIntelTypes.CategoryEnum.CATEGORY_OTHER]: theme.colors.neutral50,
    [CallIntelTypes.CategoryEnum.CATEGORY_SCHEDULING]: theme.colors.primary50,
    [CallIntelTypes.CategoryEnum.CATEGORY_UNKNOWN]: theme.colors.neutral50,
    [CallIntelTypes.CategoryEnum.CATEGORY_VENDOR]: theme.colors.neutral50,
  },

  schedulingOutcome: {
    [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNKNOWN]: theme.colors.neutral80,
    [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: theme.colors.success50,
    [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED]: theme.colors.warning40,
  },

  schedulingOutcomeV2: {
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNKNOWN]: theme.colors.neutral5,
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL]: theme.colors.primary60,
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED]: theme.colors.warning30,
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]: theme.colors.secondary.seaweed50,
  },

  serviceQualityFlags: {
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: theme.colors.tangerine50,
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: theme.colors.rose50,
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: theme.colors.warning50,
  },
};

const DEFAULT_CHART_OUTLINE_COLORS: DataLabelsAndColors = {
  appointmentTypes: {
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_COSMETIC]: theme.colors.secondary.seaweed50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_EMERGENCY]: theme.colors.critical50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_HYGIENE]: theme.colors.indigo70,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_IMAGING]: theme.colors.tangerine60,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_ORTHODONTICS]: theme.colors.secondary.eggplant50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE]: theme.colors.rose60,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_RESTORATIVE]: theme.colors.tangerine50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SURGERY]: theme.colors.secondary.seaweed60,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN]: theme.colors.neutral40,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SICK_PET]: theme.colors.tangerine50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE_CARE]: theme.colors.rose60,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DENTAL]: theme.colors.indigo70,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTICS]: theme.colors.secondary.seaweed50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_END_OF_LIFE_CARE]: theme.colors.secondary.eggplant50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_EYE_EXAM]: theme.colors.indigo70,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_VISION_CORRECTION]: theme.colors.tangerine50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_MEDICAL_EYE_CARE]: theme.colors.rose60,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SPECIALTY_SERVICES]: theme.colors.secondary.eggplant50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_CONSULTATION]: theme.colors.rose60,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_ROUTINE_CHECKUP]: theme.colors.indigo70,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTIC_TESTS]: theme.colors.tangerine50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_OTHER]: theme.colors.neutral50,
  },

  categories: {
    [CallIntelTypes.CategoryEnum.CATEGORY_BILLING]: theme.colors.success50,
    [CallIntelTypes.CategoryEnum.CATEGORY_COMPLAINT]: theme.colors.warning40,
    [CallIntelTypes.CategoryEnum.CATEGORY_EMERGENCY]: theme.colors.critical50,
    [CallIntelTypes.CategoryEnum.CATEGORY_MEDICATION]: theme.colors.secondary.eggplant60,
    [CallIntelTypes.CategoryEnum.CATEGORY_OTHER]: theme.colors.neutral50,
    [CallIntelTypes.CategoryEnum.CATEGORY_SCHEDULING]: theme.colors.primary70,
    [CallIntelTypes.CategoryEnum.CATEGORY_UNKNOWN]: theme.colors.neutral40,
    [CallIntelTypes.CategoryEnum.CATEGORY_VENDOR]: theme.colors.neutral60,
  },

  sentiments: {
    [CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE]: theme.colors.critical60,
    [CallIntelTypes.SentimentEnum.SENTIMENT_NEUTRAL]: theme.colors.primary40,
    [CallIntelTypes.SentimentEnum.SENTIMENT_POSITIVE]: theme.colors.success50,
    [CallIntelTypes.SentimentEnum.SENTIMENT_UNKNOWN]: theme.colors.neutral50,
  },

  schedulingOutcome: {
    [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNKNOWN]: theme.colors.neutral10,
    [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: theme.colors.success60,
    [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED]: theme.colors.warning40,
  },

  schedulingOutcomeV2: {
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNKNOWN]: theme.colors.neutral5,
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL]: theme.colors.primary60,
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED]: theme.colors.warning30,
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]: theme.colors.secondary.seaweed50,
  },

  serviceQualityFlags: {
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: theme.colors.tangerine50,
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: theme.colors.rose40,
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: theme.colors.warning40,
  },
};

const DEFAULT_CHIP_VARIANTS: CallIntelChipVariant = {
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN]: {
    variant: 'neutral',
  },

  // Dental-specific
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_IMAGING]: {
    variantColor: theme.colors.tangerine40,
  },
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_COSMETIC]: {
    variantColor: theme.colors.secondary.seaweed30,
  },
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_ORTHODONTICS]: {
    variantColor: theme.colors.secondary.eggplant30,
  },
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE]: {
    variantColor: theme.colors.rose40,
  },
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_HYGIENE]: {
    variantColor: theme.colors.indigo50,
  },
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_RESTORATIVE]: {
    variantColor: theme.colors.tangerine30,
  },

  // Deprecated: Emergency (kept for backward compatibility)
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_EMERGENCY]: {
    variantColor: theme.colors.critical30,
  },

  // Vet-specific
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SICK_PET]: {
    variantColor: theme.colors.tangerine30,
  },
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE_CARE]: {
    variantColor: theme.colors.rose40,
  },
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DENTAL]: {
    variantColor: theme.colors.indigo50,
  },
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTICS]: {
    variantColor: theme.colors.secondary.seaweed30,
  },
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_END_OF_LIFE_CARE]: {
    variantColor: theme.colors.secondary.eggplant30,
  },

  // Optometry-specific
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_EYE_EXAM]: {
    variantColor: theme.colors.indigo50,
  },
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_VISION_CORRECTION]: {
    variantColor: theme.colors.tangerine30,
  },
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_MEDICAL_EYE_CARE]: {
    variantColor: theme.colors.rose40,
  },
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SPECIALTY_SERVICES]: {
    variantColor: theme.colors.secondary.eggplant30,
  },

  // Default appointment types
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_CONSULTATION]: {
    variantColor: theme.colors.rose40,
  },
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_ROUTINE_CHECKUP]: {
    variantColor: theme.colors.indigo50,
  },
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTIC_TESTS]: {
    variantColor: theme.colors.tangerine30,
  },

  // Common across all verticals
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SURGERY]: {
    variantColor: theme.colors.secondary.seaweed40,
  },
  [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_OTHER]: {
    variantColor: theme.colors.neutral30,
  },

  // Categories
  [CallIntelTypes.CategoryEnum.CATEGORY_BILLING]: {
    variantColor: theme.colors.success30,
  },
  [CallIntelTypes.CategoryEnum.CATEGORY_COMPLAINT]: {
    variantColor: theme.colors.warning20,
  },
  [CallIntelTypes.CategoryEnum.CATEGORY_EMERGENCY]: {
    variantColor: theme.colors.critical30,
  },
  [CallIntelTypes.CategoryEnum.CATEGORY_MEDICATION]: {
    variantColor: theme.colors.secondary.eggplant40,
  },
  [CallIntelTypes.CategoryEnum.CATEGORY_OTHER]: {
    variantColor: theme.colors.neutral30,
  },
  [CallIntelTypes.CategoryEnum.CATEGORY_SCHEDULING]: {
    variantColor: theme.colors.primary50,
  },
  [CallIntelTypes.CategoryEnum.CATEGORY_UNKNOWN]: {
    variant: 'neutral',
  },
  [CallIntelTypes.CategoryEnum.CATEGORY_VENDOR]: {
    variantColor: theme.colors.neutral40,
  },

  // Contact Types
  [CallIntelTypes.ContactTypeEnum.CONTACT_EXISTING_PATIENT]: {
    variant: 'primary',
  },
  [CallIntelTypes.ContactTypeEnum.CONTACT_NEW_PATIENT]: {
    variant: 'warn',
  },
  [CallIntelTypes.ContactTypeEnum.CONTACT_NOT_A_PATIENT]: {
    variant: 'neutral',
  },
  [CallIntelTypes.ContactTypeEnum.CONTACT_UNKNOWN]: {
    variant: 'neutral',
  },

  // Task Types
  [TaskType.TYPE_BILLING]: {
    variantColor: theme.colors.success30,
  },
  [TaskType.TYPE_INSURANCE]: {
    variantColor: theme.colors.secondary.eggplant60,
  },
  [TaskType.TYPE_OTHER]: {
    variantColor: theme.colors.neutral30,
  },
  [TaskType.TYPE_PATIENT_CARE]: {
    variantColor: theme.colors.warning30,
  },
  [TaskType.TYPE_SCHEDULING]: {
    variantColor: theme.colors.primary50,
  },
  [TaskType.TYPE_WAITLIST]: {
    variantColor: theme.colors.critical50,
  },

  // Sentiment
  [CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE]: {
    variant: 'critical',
    leftIconName: 'emoji-sad-small',
  },
  [CallIntelTypes.SentimentEnum.SENTIMENT_NEUTRAL]: {
    variant: 'primary',
    leftIconName: 'emoji-neutral-small',
  },
  [CallIntelTypes.SentimentEnum.SENTIMENT_POSITIVE]: {
    variant: 'success',
    leftIconName: 'emoji-small',
  },
  [CallIntelTypes.SentimentEnum.SENTIMENT_UNKNOWN]: {
    variant: 'neutral',
    leftIconName: 'alert-small',
  },

  //Scheduling Outcome
  [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: {
    variant: 'success',
    leftIconName: 'check-small',
  },

  //Scheduling Outcome V2

  [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL]: {
    variant: 'primary',
    leftIconName: 'check-small',
    fullWidth: true,
    leftIconColor: theme.colors.primary70,
    textColor: theme.colors.primary70,
  },
  [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED]: {
    variant: 'warn',
    leftIconName: 'x-small',
    leftIconColor: theme.colors.warning70,
    textColor: theme.colors.warning70,
    backgroundColor: theme.colors.warning10,
    fullWidth: true,
  },
  [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]: {
    variant: 'seaweed',
    leftIconName: 'calendar-small',
    leftIconColor: theme.colors.secondary.seaweed70,
    textColor: theme.colors.secondary.seaweed70,
    fullWidth: true,
  },
  [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_NO_OPPORTUNITY]: {
    variant: 'neutral',
    leftIconName: 'feedback-bad-small',
    leftIconColor: theme.colors.neutral80,
    textColor: theme.colors.neutral70,
    fullWidth: true,
  },

  // Service Quality Flags
  [CallIntelTypes.ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: {
    variant: 'default',
    leftIconName: 'wellness-small',
    fullWidth: true,
    textColor: theme.colors.neutral90,
    leftIconColor: theme.colors.tangerine40,
  },
  [CallIntelTypes.ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: {
    variant: 'default',
    leftIconName: 'phone-alert-small',
    fullWidth: true,
    textColor: theme.colors.neutral90,
    leftIconColor: theme.colors.rose50,
  },
  [CallIntelTypes.ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: {
    variant: 'default',
    leftIconName: 'user-escalation-small',
    fullWidth: true,
    textColor: theme.colors.neutral90,
    leftIconColor: theme.colors.warning30,
  },
};

const DEFAULT_DATA_COLORS: DataLabelsAndColors = {
  appointmentTypes: {
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_COSMETIC]: theme.colors.secondary.seaweed30,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_EMERGENCY]: theme.colors.critical30,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_HYGIENE]: theme.colors.indigo50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_IMAGING]: theme.colors.tangerine40,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_ORTHODONTICS]: theme.colors.secondary.eggplant30,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE]: theme.colors.rose40,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_RESTORATIVE]: theme.colors.tangerine30,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SURGERY]: theme.colors.secondary.seaweed40,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN]: theme.colors.neutral20,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SICK_PET]: theme.colors.tangerine30,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE_CARE]: theme.colors.rose40,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DENTAL]: theme.colors.indigo50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTICS]: theme.colors.secondary.seaweed30,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_END_OF_LIFE_CARE]: theme.colors.secondary.eggplant30,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_EYE_EXAM]: theme.colors.indigo50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_VISION_CORRECTION]: theme.colors.tangerine30,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_MEDICAL_EYE_CARE]: theme.colors.rose40,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SPECIALTY_SERVICES]: theme.colors.secondary.eggplant30,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_CONSULTATION]: theme.colors.rose40,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_ROUTINE_CHECKUP]: theme.colors.indigo50,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTIC_TESTS]: theme.colors.tangerine30,
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_OTHER]: theme.colors.neutral30,
  },

  categories: {
    [CallIntelTypes.CategoryEnum.CATEGORY_BILLING]: theme.colors.success30,
    [CallIntelTypes.CategoryEnum.CATEGORY_COMPLAINT]: theme.colors.warning20,
    [CallIntelTypes.CategoryEnum.CATEGORY_EMERGENCY]: theme.colors.critical30,
    [CallIntelTypes.CategoryEnum.CATEGORY_MEDICATION]: theme.colors.secondary.eggplant40,
    [CallIntelTypes.CategoryEnum.CATEGORY_OTHER]: theme.colors.neutral30,
    [CallIntelTypes.CategoryEnum.CATEGORY_SCHEDULING]: theme.colors.primary50,
    [CallIntelTypes.CategoryEnum.CATEGORY_UNKNOWN]: theme.colors.neutral20,
    [CallIntelTypes.CategoryEnum.CATEGORY_VENDOR]: theme.colors.neutral40,
  },

  sentiments: {
    [CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE]: theme.colors.critical40,
    [CallIntelTypes.SentimentEnum.SENTIMENT_NEUTRAL]: theme.colors.primary20,
    [CallIntelTypes.SentimentEnum.SENTIMENT_POSITIVE]: theme.colors.success30,
    [CallIntelTypes.SentimentEnum.SENTIMENT_UNKNOWN]: theme.colors.neutral30,
  },

  schedulingOutcome: {
    [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNKNOWN]: theme.colors.neutral5,
    [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: theme.colors.success40,
    [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED]: theme.colors.warning20,
  },

  schedulingOutcomeV2: {
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNKNOWN]: theme.colors.neutral50,
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL]: theme.colors.primary40,
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED]: theme.colors.warning20,
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]: theme.colors.secondary.seaweed30,
  },

  serviceQualityFlags: {
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: theme.colors.tangerine30,
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: theme.colors.rose20,
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: theme.colors.warning20,
  },
};

const DEFAULT_DATA_HIGHLIGHT_COLORS: DataLabelsAndColors = {
  serviceQualityFlags: {
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_EXCELLENT_RESOLUTION]: theme.colors.primary5,
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_UNRESOLVED_ISSUE]: theme.colors.critical5,
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_UNKNOWN]: theme.colors.primary5,
  },
};

const DEFAULT_DATA_LABELS = {
  appointmentTypes: {
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_UNKNOWN]: t('Unknown'),
    // Dental-specific
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_IMAGING]: t('Imaging'),
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_COSMETIC]: t('Cosmetic'),
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_ORTHODONTICS]: t('Orthodontics'),
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE]: t('Preventive'),
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_HYGIENE]: t('Hygiene'),
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_RESTORATIVE]: t('Restorative'),
    // Deprecated: Emergency (kept for backward compatibility)
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_EMERGENCY]: t('Emergency'),

    // Vet-specific
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SICK_PET]: t('Sick Pet'),
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_PREVENTIVE_CARE]: t('Preventive Care'),
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DENTAL]: t('Dental'),
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTICS]: t('Diagnostics'),
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_END_OF_LIFE_CARE]: t('End of Life Care'),

    // Optometry-specific
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_EYE_EXAM]: t('Eye Exam'),
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_VISION_CORRECTION]: t('Vision Correction'),
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_MEDICAL_EYE_CARE]: t('Medical Eye Care'),
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SPECIALTY_SERVICES]: t('Specialty Services'),

    // Default appointment types
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_CONSULTATION]: t('Consultation'),
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_ROUTINE_CHECKUP]: t('Routine Checkup'),
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_DIAGNOSTIC_TESTS]: t('Diagnostic Tests'),

    // Common across all verticals
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_SURGERY]: t('Surgery'),
    [CallIntelTypes.AppointmentTypeEnum.APPOINTMENT_TYPE_OTHER]: t('Other'),
  },

  categories: {
    [CallIntelTypes.CategoryEnum.CATEGORY_BILLING]: t('Billing'),
    [CallIntelTypes.CategoryEnum.CATEGORY_COMPLAINT]: t('Complaint'),
    [CallIntelTypes.CategoryEnum.CATEGORY_EMERGENCY]: t('Emergency'),
    [CallIntelTypes.CategoryEnum.CATEGORY_MEDICATION]: t('Medication'),
    [CallIntelTypes.CategoryEnum.CATEGORY_OTHER]: t('Other'),
    [CallIntelTypes.CategoryEnum.CATEGORY_SCHEDULING]: t('Scheduling'),
    [CallIntelTypes.CategoryEnum.CATEGORY_UNKNOWN]: t('Unknown'),
    [CallIntelTypes.CategoryEnum.CATEGORY_VENDOR]: t('Vendor'),
  },

  sentiments: {
    [CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE]: t('Unhappy'),
    [CallIntelTypes.SentimentEnum.SENTIMENT_NEUTRAL]: t('Neutral'),
    [CallIntelTypes.SentimentEnum.SENTIMENT_POSITIVE]: t('Happy'),
    [CallIntelTypes.SentimentEnum.SENTIMENT_UNKNOWN]: t('Unknown'),
  },

  sentimentsWithEmoji: {
    [CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE]: `😒 ${t('Unhappy')}`,
    [CallIntelTypes.SentimentEnum.SENTIMENT_NEUTRAL]: `😐 ${t('Neutral')}`,
    [CallIntelTypes.SentimentEnum.SENTIMENT_POSITIVE]: `😊 ${t('Happy')}`,
    [CallIntelTypes.SentimentEnum.SENTIMENT_UNKNOWN]: `🤔 ${t('Unknown')}`,
  },

  serviceQualityFlags: {
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: t('Treatment Concern'),
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: t('Unresolved Contact Attempt'),
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: t('Manager Escalation'),
  },

  serviceQualityLabels: {
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: t('Treatment Concerns'),
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: t('Unresolved Contact Attempts'),
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: t('Manager Escalations'),
  },

  taskTypes: {
    [TaskType.TYPE_BILLING]: t('Billing'),
    [TaskType.TYPE_INSURANCE]: t('Insurance'),
    [TaskType.TYPE_OTHER]: t('Other'),
    [TaskType.TYPE_PATIENT_CARE]: t('Patient Care'),
    [TaskType.TYPE_SCHEDULING]: t('Scheduling'),
    [TaskType.TYPE_WAITLIST]: t('Waitlist'),
  },

  schedulingOutcome: {
    [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: t('Scheduled'), //TODO: Remove this and use the V2 version instead after GA
    [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED]: t('Unscheduled'), //TODO: Remove this and use the V2 version instead after GA
  },

  schedulingOutcomeV2: {
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL]: t('Scheduled On Call'),
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED]: t('Unscheduled'),
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]: t('Recovered Opportunity'),
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_NO_OPPORTUNITY]: t('No Opportunity'),
  },

  schedulingOutcomeV2Alternate: {
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL]: t('Scheduled On Call'),
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED]: t('Still Unscheduled'),
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]: t('Recovered Opportunities'),
    [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_NO_OPPORTUNITY]: t('No Opportunity'),
  },
};

const DEFAULT_HEADER_CHIP_VARIANTS: CallIntelChipVariant = {
  // Task Types
  [TaskType.TYPE_BILLING]: {
    variant: 'success',
    variantColor: theme.colors.success5,
    textColor: theme.colors.success50,
  },
  [TaskType.TYPE_INSURANCE]: {
    variant: 'eggplant',
    variantColor: theme.colors.secondary.eggplant5,
    textColor: theme.colors.secondary.eggplant50,
  },
  [TaskType.TYPE_OTHER]: {
    variant: 'neutral',
    variantColor: theme.colors.neutral5,
    textColor: theme.colors.neutral50,
  },
  [TaskType.TYPE_PATIENT_CARE]: {
    variant: 'warn',
    variantColor: theme.colors.warning5,
    textColor: theme.colors.warning60,
  },
  [TaskType.TYPE_SCHEDULING]: {
    variant: 'primary',
    variantColor: theme.colors.primary5,
    textColor: theme.colors.primary50,
  },
  [TaskType.TYPE_WAITLIST]: {
    variant: 'critical',
    variantColor: theme.colors.critical5,
    textColor: theme.colors.critical50,
  },
};

const DEFAULT_PAGE_LABELS: PageLabels = {
  serviceQualityFlags: {
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_INEFFECTIVE_TREATMENT]: t('Treatment Concerns'),
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_REPEAT_CALLER]: t('Unresolved Contact Attempts'),
    [CallIntelTypes.ServiceQualityFlagEnum.FLAG_CALL_ESCALATION]: t('Manager Escalations'),
  },
};

interface CallIntelDefaultsStore {
  availableAppointmentTypes: CallIntelTypes.AppointmentTypeEnum[];
  availableCategories: CallIntelTypes.CategoryEnum[];
  chartLabelColors: DataLabelsAndColors;
  chartOutlineColors: DataLabelsAndColors;
  chipVariants: CallIntelChipVariant;
  dataColors: DataLabelsAndColors;
  dataHighlightColors: DataLabelsAndColors;
  dataLabels: DataLabels;
  headerChipVariants: CallIntelChipVariant;
  pageLabels: PageLabels;

  setAvailableAppointmentTypes: (availableAppointmentTypes: CallIntelTypes.AppointmentTypeEnum[]) => void;
  setAvailableCategories: (availableCategories: CallIntelTypes.CategoryEnum[]) => void;
  setChartLabelColors: (chartLabelColors: DataLabelsAndColors) => void;
  setChartOutlineColors: (chartOutlineColors: DataLabelsAndColors) => void;
  setChipVariants: (chipVariants: CallIntelChipVariant) => void;
  setDataColors: (dataColors: DataLabelsAndColors) => void;
  setDataHighlightColors: (dataHighlightColors: DataLabelsAndColors) => void;
  setHeaderChipVariants: (headerChipVariants: CallIntelChipVariant) => void;
  setPageLabels: (pageLabels: PageLabels) => void;
}

const useCallIntelDefaultsStore = createStoreWithSubscribe<CallIntelDefaultsStore>(
  (set) => ({
    availableAppointmentTypes: DEFAULT_APPOINTMENT_TYPES,
    availableCategories: DEFAULT_CATEGORIES,
    chartLabelColors: DEFAULT_CHART_LABEL_COLORS,
    chartOutlineColors: DEFAULT_CHART_OUTLINE_COLORS,
    chipVariants: DEFAULT_CHIP_VARIANTS,
    dataColors: DEFAULT_DATA_COLORS,
    dataHighlightColors: DEFAULT_DATA_HIGHLIGHT_COLORS,
    dataLabels: DEFAULT_DATA_LABELS,
    headerChipVariants: DEFAULT_HEADER_CHIP_VARIANTS,
    pageLabels: DEFAULT_PAGE_LABELS,

    setAvailableAppointmentTypes: (availableAppointmentTypes) => set({ availableAppointmentTypes }),
    setAvailableCategories: (availableCategories) => set({ availableCategories }),
    setChartLabelColors: (chartLabelColors) => set({ chartLabelColors }),
    setChartOutlineColors: (chartOutlineColors) => set({ chartOutlineColors }),
    setChipVariants: (chipVariants) => set({ chipVariants }),
    setDataColors: (dataColors) => set({ dataColors }),
    setDataHighlightColors: (dataHighlightColors) => set({ dataHighlightColors }),
    setHeaderChipVariants: (headerChipVariants) => set({ headerChipVariants }),
    setPageLabels: (pageLabels) => set({ pageLabels }),
  }),
  { name: 'CallIntelDefaultsStore', trace: true }
);

export const useCallIntelContactTypesLabel = () => {
  const { t } = useTranslation('analytics');
  const { patientCapitalized: patientCapitalizedToken } = useTranslationTokens();

  return useMemo<Record<CallIntelTypes.ContactTypeEnum, string>>(() => {
    return {
      [CallIntelTypes.ContactTypeEnum.CONTACT_EXISTING_PATIENT]: t('Existing {{patientCapitalizedToken}}', {
        patientCapitalizedToken,
      }),
      [CallIntelTypes.ContactTypeEnum.CONTACT_NEW_PATIENT]: t('New {{patientCapitalizedToken}}', {
        patientCapitalizedToken,
      }),
      [CallIntelTypes.ContactTypeEnum.CONTACT_NOT_A_PATIENT]: t('Not a {{patientCapitalizedToken}}', {
        patientCapitalizedToken,
      }),
      [CallIntelTypes.ContactTypeEnum.CONTACT_UNKNOWN]: t('Unavailable Data'),
    };
  }, [t, patientCapitalizedToken]);
};

export const useCallIntelDefaultsShallowStore = createShallowStore<CallIntelDefaultsStore>(useCallIntelDefaultsStore);
